import { useNavigate } from "react-router-dom";
import {
  Dumbbell,
  Moon,
  Crown,
  Play,
  AlertTriangle,
  Heart,
  Sparkles,
  Clock,
  Check,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import {
  useMemberStatus,
  useMemberTrainingPlan,
  useTodayWorkout,
} from "@/hooks/useAppState";
import TodayTrainingSwitcher from "@/components/TodayTrainingSwitcher";

export default function HomeWorkoutCard({
  setAerobicOpen,
}: {
  setAerobicOpen: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const { member } = useMemberStatus();
  const {
    memberPlan,
    getTodayWeekDay,
    todayAerobicCompleted,
    markTodayAerobicComplete,
  } = useMemberTrainingPlan();
  const todayMemberDay = getTodayWeekDay();
  const todayWorkout = useTodayWorkout();
  const handleStartTraining = () => {
    if (todayWorkout.hasPlan) navigate("/day/" + todayWorkout.dayId);
  };
  const handleGoTraining = () => navigate("/training?tab=plans");
  return (
    <>
      <TodayTrainingSwitcher />
      {/* 🔴 双重校验：必须同时满足「会员身份 + 会员锻炼计划存在」才显示会员专属卡 */}
      {member.isMember && memberPlan ? (
        todayMemberDay?.type === "strength" ? (
          todayWorkout.shouldRestToday ? (
            // 三项异常：建议休息
            <div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-warning/20 flex items-center justify-center shrink-0">
                  <Moon className="w-5 h-5 text-warning" />
                </div>
                <div className="flex-1">
                  <p className="text-[11px] text-primary font-semibold mb-1 flex items-center gap-1">
                    <Crown className="w-3 h-3" /> 会员专属·今日建议
                  </p>
                  <h2 className="text-base font-bold text-foreground">
                    今日建议休息
                  </h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    身体状态不太理想，训练顺延到明天
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-warning/15 text-warning">
                  心率偏高
                </span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-info/15 text-info">
                  睡眠不足
                </span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-destructive/15 text-destructive">
                  心情不佳
                </span>
              </div>
            </div>
          ) : (
            <div
              onClick={handleStartTraining}
              className="cursor-pointer active:scale-[0.98] transition-transform"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <p className="text-[11px] text-primary font-semibold mb-1 flex items-center gap-1">
                    <Crown className="w-3 h-3" /> 会员专属·今日训练
                  </p>
                  <h2 className="text-lg font-bold text-foreground">
                    {todayMemberDay.label}
                  </h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {todayMemberDay.subtitle}
                  </p>
                </div>
                <div className="w-12 h-9 rounded-lg bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center shadow-inner">
                  <Dumbbell className="w-5 h-5 text-white" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                预计 {todayWorkout.durationMin} 分钟 · {todayWorkout.totalSets}{" "}
                正式组
              </p>
              {todayWorkout.volumeReductionPercent > 0 && (
                <div className="flex items-center gap-1.5 mb-3 px-2.5 py-1.5 rounded-lg bg-warning/10">
                  <AlertTriangle className="w-3.5 h-3.5 text-warning shrink-0" />
                  <span className="text-[11px] text-warning font-medium">
                    今日已自动降量 {todayWorkout.volumeReductionPercent}%
                  </span>
                </div>
              )}
              <button
                className={`w-full py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-1.5 ${
                  todayWorkout.status === "completed"
                    ? "bg-success/15 text-success"
                    : "btn-primary-gradient text-white"
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleStartTraining();
                }}
              >
                {todayWorkout.status === "completed" ? (
                  <>✓ 已完成</>
                ) : todayWorkout.status === "in-progress" ? (
                  <>
                    <Play className="w-4 h-4 fill-current" /> 继续训练
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 fill-current" /> 开始训练
                  </>
                )}
              </button>
            </div>
          )
        ) : (
          <div
            className="cursor-pointer active:scale-[0.98] transition-transform"
            onClick={() =>
              todayMemberDay &&
              todayMemberDay.type !== "strength" &&
              setAerobicOpen(true)
            }
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center shrink-0">
                {todayMemberDay?.type === "cardio" ||
                todayMemberDay?.type === "hiit" ? (
                  <Heart className="w-5 h-5 text-warning" />
                ) : todayMemberDay?.type === "active-rest" ? (
                  <Sparkles className="w-5 h-5 text-muted-foreground" />
                ) : (
                  <Moon className="w-5 h-5 text-muted-foreground" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-[11px] text-primary font-semibold mb-1 flex items-center gap-1">
                  <Crown className="w-3 h-3" /> 会员专属·今日安排
                </p>
                <h2 className="text-base font-bold text-foreground">
                  {todayMemberDay?.label || "休息日"}
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {todayMemberDay?.subtitle || "好好休息，明天继续"}
                </p>
                {todayWorkout.durationMin > 0 && (
                  <p className="text-[11px] text-muted-foreground mt-2 flex items-center gap-1">
                    <Clock className="w-3 h-3" />约 {todayWorkout.durationMin}{" "}
                    分钟
                  </p>
                )}
              </div>
            </div>
            {todayWorkout.shouldRestToday &&
              todayMemberDay?.type !== "rest" && (
                <div className="flex items-center gap-1.5 mt-3 px-2.5 py-1.5 rounded-lg bg-warning/10">
                  <AlertTriangle className="w-3.5 h-3.5 text-warning shrink-0" />
                  <span className="text-[11px] text-warning font-medium">
                    今日建议休息，已调整为恢复性安排
                  </span>
                </div>
              )}
            {!todayWorkout.shouldRestToday &&
              todayWorkout.volumeReductionPercent > 0 &&
              todayMemberDay?.type !== "rest" && (
                <div className="flex items-center gap-1.5 mt-3 px-2.5 py-1.5 rounded-lg bg-warning/10">
                  <AlertTriangle className="w-3.5 h-3.5 text-warning shrink-0" />
                  <span className="text-[11px] text-warning font-medium">
                    今日已自动降量 {todayWorkout.volumeReductionPercent}%
                  </span>
                </div>
              )}
            {/* 有氧/主动恢复完成状态 + 快捷完成按钮 */}
            {(todayMemberDay?.type === "cardio" ||
              todayMemberDay?.type === "hiit" ||
              todayMemberDay?.type === "active-rest") &&
              !todayWorkout.shouldRestToday && (
                <div className="mt-3 pt-3 border-t border-border/40">
                  {todayAerobicCompleted ? (
                    <div className="flex items-center justify-center gap-2 py-2 rounded-xl bg-success/10 text-success">
                      <CheckCircle2 className="w-4 h-4" />
                      <span className="text-sm font-bold">已完成 ✓</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setAerobicOpen(true);
                        }}
                        className="flex-1 py-2 rounded-xl btn-primary-gradient text-white text-xs font-bold flex items-center justify-center gap-1"
                      >
                        <Play className="w-3.5 h-3.5 fill-current" />
                        开始训练
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          markTodayAerobicComplete();
                          toast.success("已标记今日有氧完成");
                        }}
                        className="flex-1 py-2 rounded-xl bg-muted text-foreground text-xs font-bold active:scale-[0.98] transition-transform flex items-center justify-center gap-1"
                      >
                        <Check className="w-3.5 h-3.5" />
                        已练完
                      </button>
                    </div>
                  )}
                </div>
              )}
          </div>
        )
      ) : (
        <div>
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/15 to-secondary/10 flex items-center justify-center shrink-0">
              <Dumbbell className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
              <h2 className="text-base font-bold text-foreground mb-1">
                今天还没有训练安排
              </h2>
              <p className="text-xs text-muted-foreground leading-relaxed">
                选择训练计划或自由搭配动作，开始今天的训练吧
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <button
              onClick={handleGoTraining}
              className="btn-primary-gradient py-2.5 rounded-xl text-white text-xs font-bold flex items-center justify-center gap-1"
            >
              <Dumbbell className="w-3.5 h-3.5" /> 进入训练
            </button>
          </div>
        </div>
      )}
    </>
  );
}
